import { ItemInstance, ItemDefinition, ItemRarity, ItemBonuses, ItemSlot } from '../types/Item.types';
import { ResourceStock, COMMON_RESOURCES, ResourceType } from '../systems/ResourceManager';

// === HELPERS COFFRE DU CAMP ===
// Tri des items, formatage des bonus inline, calcul déterministe du recyclage.
// Logique extraite de StorageScene pour être unitairement testable.

/** Ordre d'affichage canonique des slots dans le coffre */
export const SLOT_ORDER: readonly ItemSlot[] = [
  'weapon', 'armor', 'belt', 'ring', 'boots', 'backpack',
];

/** Poids de tri par rareté (rare en premier) */
const RARITY_WEIGHT: Record<ItemRarity, number> = {
  rare: 0,
  uncommon: 1,
  common: 2,
};

/**
 * Trie une liste d'items par : slot (weapon → backpack) puis rareté
 * (rare → uncommon → common) puis nom alphabétique.
 *
 * Renvoie une nouvelle liste, ne mute pas l'entrée. Les items dont la
 * définition est introuvable sont relégués en fin de liste.
 */
export function sortStorageItems(
  items: ItemInstance[],
  definitions: Record<string, ItemDefinition>,
): ItemInstance[] {
  return [...items].sort((a, b) => {
    const da = definitions[a.definitionId];
    const db = definitions[b.definitionId];
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;

    const slotDiff = SLOT_ORDER.indexOf(da.slot) - SLOT_ORDER.indexOf(db.slot);
    if (slotDiff !== 0) return slotDiff;

    const rarityDiff = RARITY_WEIGHT[da.rarity] - RARITY_WEIGHT[db.rarity];
    if (rarityDiff !== 0) return rarityDiff;

    return da.name.localeCompare(db.name, 'fr', { sensitivity: 'base' });
  });
}

/**
 * Formate les bonus d'un item en une chaîne courte pour affichage inline.
 * Ex: "ATK +2  DEF +1  PA +1". Renvoie une chaîne vide si aucun bonus.
 */
export function formatItemBonusesInline(bonuses: Partial<ItemBonuses>): string {
  const parts: string[] = [];
  const sign = (n: number): string => (n > 0 ? `+${n}` : `${n}`);
  if (bonuses.attack)   parts.push(`ATK ${sign(bonuses.attack)}`);
  if (bonuses.defense)  parts.push(`DEF ${sign(bonuses.defense)}`);
  if (bonuses.maxHp)    parts.push(`HP ${sign(bonuses.maxHp)}`);
  if (bonuses.pa)       parts.push(`PA ${sign(bonuses.pa)}`);
  if (bonuses.pm)       parts.push(`PM ${sign(bonuses.pm)}`);
  if (bonuses.range)    parts.push(`Portée ${sign(bonuses.range)}`);
  if (bonuses.capacity) parts.push(`Sac ${sign(bonuses.capacity)}`);
  return parts.join('  ');
}

/**
 * Hash stable d'une chaîne (FNV-1a 32 bits, non cryptographique).
 * Utilisé pour rendre le recyclage déterministe par definitionId.
 */
function hashDefinitionId(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Calcule les ressources obtenues en recyclant un item — déterministe.
 *
 * Avant 0.38.9, le résultat était calculé à chaque rendu via Math.random(),
 * ce qui faisait varier les ressources affichées sur le bouton entre deux
 * redraws (et entre deux items du même type). On hash maintenant le
 * definitionId pour qu'un même item rende toujours les mêmes ressources.
 */
export function getRecycleResult(definitionId: string, rarity: ItemRarity): Partial<ResourceStock> {
  const result: Partial<ResourceStock> = {};
  const seed = hashDefinitionId(definitionId);
  const pickCommon = (offset: number): ResourceType =>
    COMMON_RESOURCES[(seed + offset) % COMMON_RESOURCES.length];

  switch (rarity) {
    case 'common': {
      // 2 ressources communes (variées par item)
      for (let i = 0; i < 2; i++) {
        const t = pickCommon(i);
        result[t] = (result[t] ?? 0) + 1;
      }
      break;
    }
    case 'uncommon': {
      // 3 communes + cristal pour ~50% des items (parité du hash)
      for (let i = 0; i < 3; i++) {
        const t = pickCommon(i);
        result[t] = (result[t] ?? 0) + 1;
      }
      if ((seed & 1) === 0) result.crystal = 1;
      break;
    }
    case 'rare': {
      // 2 communes + 1 cristal garanti
      for (let i = 0; i < 2; i++) {
        const t = pickCommon(i);
        result[t] = (result[t] ?? 0) + 1;
      }
      result.crystal = 1;
      break;
    }
  }
  return result;
}
